import { FICHIER_LECTURE_EXPORT, formatDate } from '@artosera/shared';
import { Button, Checkbox } from '@artosera/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '../../api/mutation.js';
import { Archive as ArchiveIcon, Check } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import {
  archivesApi,
  cheminTelechargement,
  CLE_ARCHIVES,
  lancerTelechargement,
  type ArchivePresentee,
} from '../../api/archives.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import { estApiError } from '../../api/errors.js';
import { AuthCard } from '../AuthCard.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { oublierJeton, type DonneesArchive } from './archiveLoader.js';
import styles from './ArchivePage.module.css';

/** Intervalle de relecture pendant qu'un téléchargement est en cours. */
export const RELECTURE_MS = 4000;

/**
 * Le téléchargement SUIVI (relecture 4, R12) : l'archive et la version que
 * désigne le lien cliqué, et le nombre de téléchargements entiers de cette
 * version par ce compte AU MOMENT DU CLIC.
 */
export interface TelechargementSuivi {
  readonly id: string;
  readonly sha256: string;
  readonly avant: number;
}

/** Ce qu'on retient au clic, pour le lien résolu `cible`. */
export function suivreTelechargement(
  archives: readonly ArchivePresentee[],
  cible: { readonly id: string; readonly sha256: string },
): TelechargementSuivi {
  const vue = archives.find(
    (archive) => archive.id === cible.id && archive.sha256 === cible.sha256,
  );
  return { ...cible, avant: vue?.telechargementsParMoi ?? 0 };
}

/**
 * Le téléchargement lancé est-il arrivé ? Seulement quand CETTE archive, dans
 * CETTE version, compte un téléchargement entier de plus qu'au clic : ni une
 * autre archive, ni un téléchargement antérieur au clic ne clôt le suivi.
 */
export function telechargementArrive(
  archives: readonly ArchivePresentee[],
  suivi: TelechargementSuivi,
): boolean {
  return archives.some(
    (archive) =>
      archive.id === suivi.id &&
      archive.sha256 === suivi.sha256 &&
      archive.telechargementsParMoi > suivi.avant,
  );
}

function taille(octets: number, langue: string): string {
  const mio = octets / (1024 * 1024);
  if (mio >= 1024) {
    return `${(mio / 1024).toLocaleString(langue, { maximumFractionDigits: 1 })} Gio`;
  }
  return `${mio.toLocaleString(langue, { maximumFractionDigits: 1 })} Mio`;
}

function date(iso: string | null, langue: string): string {
  return iso === null ? '—' : formatDate(new Date(iso), langue === 'en' ? 'en' : 'fr', 'long');
}

/**
 * `/archive` — « Archive de votre galerie » (ARTO-54). Le membre de la
 * direction télécharge l'archive par son lien nominatif, la vérifie, puis
 * accuse réception de CETTE version (empreinte SHA-256). Seul l'accusé
 * autorise la purge d'une galerie résiliée ; le téléchargement seul ne suffit
 * pas.
 */
export function ArchivePage(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { jeton } = useLoaderData<DonneesArchive>();
  const client = useQueryClient();
  /** Le téléchargement suivi ; `null` : aucun. */
  const [attente, setAttente] = useState<TelechargementSuivi | null>(null);
  const [stepUp, setStepUp] = useState<null | (() => void)>(null);

  const requete = useQuery({
    queryKey: CLE_ARCHIVES,
    queryFn: archivesApi.lister,
    refetchInterval: (query) => {
      if (attente === null) return false;
      return telechargementArrive(query.state.data?.archives ?? [], attente) ? false : RELECTURE_MS;
    },
  });
  const archives = requete.data?.archives ?? [];
  const mfaRequise = archives.some((archive) => archive.mfaRequise);
  // L'archive et la version du lien, résolues d'avance : le clic reste un
  // geste synchrone, et le suivi porte sur CE lien.
  const lien = useQuery({
    queryKey: [...CLE_ARCHIVES, 'lien', jeton],
    queryFn: () => archivesApi.resoudreLien(jeton ?? ''),
    enabled: jeton !== null,
    retry: false,
  });

  function telecharger(): void {
    if (jeton === null) return;
    const geste = (): void => {
      setAttente(lien.data === undefined ? null : suivreTelechargement(archives, lien.data));
      lancerTelechargement(cheminTelechargement(jeton));
    };
    if (mfaRequise) setStepUp(() => geste);
    else geste();
  }

  return (
    <AuthCard title={t('archive.titre')} subtitle={t('archive.sousTitre')} mur="decroche" large>
      <div className={styles.page}>
        <p className={styles.note}>{t('archive.lecture', { fichier: FICHIER_LECTURE_EXPORT })}</p>
        {jeton !== null ? (
          <Button className={styles.pleineLargeur} onClick={telecharger}>
            {t('archive.telecharger')}
          </Button>
        ) : (
          <p className={styles.note}>{t('archive.sansLien')}</p>
        )}
        {attente !== null && !telechargementArrive(archives, attente) ? (
          <p className={styles.note} role="status">
            {t('archive.telechargementEnCours')}
          </p>
        ) : null}

        {requete.isPending ? <p className={styles.note}>{t('archive.chargement')}</p> : null}
        {requete.isError ? (
          <p className={styles.erreur} role="alert">
            {t('archive.erreur')}
          </p>
        ) : null}
        {requete.isSuccess && archives.length === 0 ? (
          <p className={styles.note}>{t('archive.aucune')}</p>
        ) : null}

        {archives.length > 0 ? (
          <ul className={styles.liste}>
            {archives.map((archive) => (
              <li key={archive.id}>
                <FicheArchive
                  archive={archive}
                  langue={i18n.language}
                  demanderFacteur={(geste) => setStepUp(() => geste)}
                  surAccuse={() => {
                    oublierJeton();
                    void client.invalidateQueries({ queryKey: CLE_ARCHIVES });
                  }}
                />
              </li>
            ))}
          </ul>
        ) : null}

        <Button
          variant="secondary"
          className={styles.pleineLargeur}
          onClick={() => void requete.refetch()}
        >
          {t('archive.actualiser')}
        </Button>
      </div>

      <StepUpDialog
        open={stepUp !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setStepUp(null);
        }}
        description={t('archive.stepUp')}
        onVerifie={() => {
          const geste = stepUp;
          setStepUp(null);
          void client.invalidateQueries({ queryKey: CLE_ARCHIVES });
          geste?.();
        }}
      />
    </AuthCard>
  );
}

interface FicheArchiveProps {
  archive: ArchivePresentee;
  langue: string;
  demanderFacteur: (geste: () => void) => void;
  surAccuse: () => void;
}

function FicheArchive({
  archive,
  langue,
  demanderFacteur,
  surAccuse,
}: FicheArchiveProps): JSX.Element {
  const { t } = useTranslation('regie');
  const [verifiee, setVerifiee] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const accuse = useMutation({
    mutationFn: () => archivesApi.accuser(archive.id, archive.sha256),
    onSuccess: surAccuse,
    onError: (error) => {
      if (estErreurStepUpRequis(error)) {
        demanderFacteur(() => accuse.mutate());
        return;
      }
      setErreur(estApiError(error) ? error.message : t('archive.erreur'));
    },
  });

  const idCase = `archive-verifiee-${archive.id}`;

  return (
    <article className={styles.fiche} aria-label={archive.nomFichier}>
      <header className={styles.entete}>
        <span className={styles.pastille} aria-hidden="true">
          <ArchiveIcon size={18} strokeWidth={1.75} />
        </span>
        <h2 className={styles.nom}>{archive.galerie.nom}</h2>
      </header>
      <dl className={styles.details}>
        <dt>{t('archive.nature')}</dt>
        <dd>{archive.final ? t('archive.finale') : t('archive.copie')}</dd>
        <dt>{t('archive.preteLe')}</dt>
        <dd>{date(archive.pretLe, langue)}</dd>
        <dt>{t('archive.taille')}</dt>
        <dd>{taille(archive.octets, langue)}</dd>
        {archive.lienExpireLe !== null ? (
          <>
            <dt>{t('archive.lienExpire')}</dt>
            <dd>{date(archive.lienExpireLe, langue)}</dd>
          </>
        ) : null}
        <dt className={styles.pleineLigne}>{t('archive.empreinte')}</dt>
        <dd className={styles.empreinte}>{archive.sha256}</dd>
        {archive.etat === 'incomplet' ? (
          <>
            <dt>{t('archive.etat')}</dt>
            <dd>{t('archive.incomplete')}</dd>
          </>
        ) : null}
      </dl>

      <div className={styles.talon}>
        {archive.accuseeParMoi ? (
          <p className={styles.succes} role="status">
            <Check size={16} strokeWidth={2} aria-hidden="true" />
            {t('archive.accusee')}
          </p>
        ) : archive.telechargeeParMoi ? (
          <form
            className={styles.accuse}
            onSubmit={(event) => {
              event.preventDefault();
              setErreur(null);
              accuse.mutate();
            }}
            noValidate
          >
            <Checkbox
              id={idCase}
              label={t('archive.confirmation')}
              checked={verifiee}
              onCheckedChange={(etat) => setVerifiee(etat === true)}
            />
            {erreur !== null ? (
              <p className={styles.erreur} role="alert">
                {erreur}
              </p>
            ) : null}
            <Button
              type="submit"
              className={styles.pleineLargeur}
              disabled={!verifiee}
              loading={accuse.isPending}
            >
              {t('archive.accuser')}
            </Button>
          </form>
        ) : (
          <p className={styles.note}>{t('archive.nonTelechargee')}</p>
        )}
      </div>
    </article>
  );
}
